import { produce } from 'immer';
import { collectForwards } from '@bbkAdminComponents/experiences/offers/collect-forwards';
import { IntegrationDataType } from '@bbkAdminRedux/data-management/reducers';
import { type RestCard } from '@bbkAdminUtils/api-client/rest-experiences-api';
import {
  CtaProcessingOption,
  type CtaDetails,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import {
  OfferCategory,
  OfferCtaSubcategory,
  OfferObjective,
  type RestOffer,
} from '@bbkAdminUtils/api-client/rest-offers-api';
import {
  PeriodUnit,
  type Price,
} from '@bbkPricingPage/types/pricing-table-types';

export const DYNAMIC_OFFER_FIELD_NAMESPACE = 'dynamic_offer';
export const DYNAMIC_OFFER_ID_PLACEHOLDER = 'offer_id';
export const DYNAMIC_OFFER_NEW_PRICE_KEY = 'new_price';
export const DYNAMIC_OFFER_SAVINGS_KEY = 'savings';
export const DYNAMIC_OFFER_NEW_PRICE_LABEL = 'New Price';
export const DYNAMIC_OFFER_SAVINGS_LABEL = 'Savings';

const DYNAMIC_OFFER_FIELD_LABELS: Record<string, string> = {
  [DYNAMIC_OFFER_NEW_PRICE_KEY]: DYNAMIC_OFFER_NEW_PRICE_LABEL,
  [DYNAMIC_OFFER_SAVINGS_KEY]: DYNAMIC_OFFER_SAVINGS_LABEL,
};

export const toDynamicOfferFieldLabel = (fieldKey: string): string =>
  DYNAMIC_OFFER_FIELD_LABELS[fieldKey] ?? fieldKey;

export const toDynamicOfferFieldKey = (
  offerId: string,
  fieldName: string
): string => `${DYNAMIC_OFFER_FIELD_NAMESPACE}.${offerId}.${fieldName}`;

export const parseDynamicOfferFieldKey = (
  identifier: string | undefined
): { offerId: string; fieldName: string } | undefined => {
  if (!identifier?.startsWith(`${DYNAMIC_OFFER_FIELD_NAMESPACE}.`)) {
    return undefined;
  }
  const rest = identifier.slice(DYNAMIC_OFFER_FIELD_NAMESPACE.length + 1);
  const separator = rest.indexOf('.');
  if (separator <= 0 || separator === rest.length - 1) return undefined;
  return {
    offerId: rest.slice(0, separator),
    fieldName: rest.slice(separator + 1),
  };
};

export const isDynamicOfferFieldKey = (
  identifier: string | undefined
): identifier is string => !!parseDynamicOfferFieldKey(identifier);

export const DYNAMIC_OFFER_AUTO_VALUE = '-1';
export const DEFAULT_PRICE_VARIANT_ID = 'default';
/** Value the editor sends for Chargebee's default book. Saved as null. */
export const DEFAULT_PRICE_VARIANT_VALUE = '';

const PERIOD_UNIT_LABELS: Record<PeriodUnit, { one: string; many: string }> = {
  [PeriodUnit.day]: { one: 'Daily', many: 'days' },
  [PeriodUnit.week]: { one: 'Weekly', many: 'weeks' },
  [PeriodUnit.month]: { one: 'Monthly', many: 'months' },
  [PeriodUnit.year]: { one: 'Yearly', many: 'years' },
};

const isPeriodUnit = (value: string): value is PeriodUnit =>
  (Object.values(PeriodUnit) as string[]).includes(value);

const isMultiCtaOffer = (offerCategory?: RestOffer['category']): boolean =>
  offerCategory === OfferCategory.multi_cta;

export const isDynamicPlanChangeCta = (
  details: Partial<CtaDetails> | undefined,
  featureEnabled: boolean,
  integrationDataType?: IntegrationDataType,
  offerCategory?: RestOffer['category'],
  offerObjective?: RestOffer['objective']
): boolean =>
  featureEnabled &&
  integrationDataType === IntegrationDataType.CHARGEBEE &&
  details?.category === OfferCategory.plan_change &&
  details?.processing_option === CtaProcessingOption.billing &&
  details?.sub_category !== OfferCtaSubcategory.add_on &&
  !isMultiCtaOffer(offerCategory) &&
  offerObjective === OfferObjective.retention;

/** Drop item/frequency/toggle fields that only apply to billing plan-change CTAs. */
export const clearDynamicPlanChangeCtaFields = (
  details: Partial<CtaDetails> | undefined,
  options?: { clearPlanChangeCurrency?: boolean }
): void => {
  if (!details) return;
  details.billing_system_item_id = undefined;
  details.period = undefined;
  details.period_unit = undefined;
  details.price_variant = undefined;
  details.show_new_price_monthly = undefined;
  details.show_savings_monthly = undefined;
  if (
    options?.clearPlanChangeCurrency &&
    details.category === OfferCategory.plan_change
  ) {
    details.pinned_currency = undefined;
  }
};

/** Missing toggles display off. Save sends false so a stored true is not kept. */
export const materializeDynamicPlanChangeDetailsForSave = (
  details: Partial<CtaDetails>
): void => {
  details.show_new_price_monthly = details.show_new_price_monthly === true;
  details.show_savings_monthly = details.show_savings_monthly === true;
};

export const materializeDynamicPlanChangeOfferForSave = <
  T extends {
    cards?: RestCard[];
    category?: RestOffer['category'];
    objective?: RestOffer['objective'];
  },
>(
  offer: T,
  featureEnabled: boolean,
  integrationDataType?: IntegrationDataType
): T =>
  produce(offer, (draft) => {
    for (const card of draft.cards ?? []) {
      for (const forward of collectForwards(card)) {
        const list = forward.value?.cta_details_list?.length
          ? forward.value.cta_details_list
          : forward.value?.cta_details
            ? [forward.value.cta_details]
            : [];
        for (const details of list) {
          if (
            !details ||
            !isDynamicPlanChangeCta(
              details,
              featureEnabled,
              integrationDataType,
              draft.category,
              draft.objective
            )
          ) {
            continue;
          }
          materializeDynamicPlanChangeDetailsForSave(details);
        }
      }
    }
  });

export const toFrequencyKey = (
  period: number,
  periodUnit: PeriodUnit
): string => `${period}::${periodUnit}`;

export const parseFrequencyKey = (
  key: string | undefined
): { period: number; period_unit: PeriodUnit } | undefined => {
  if (!key) return undefined;
  const [periodStr, periodUnit] = key.split('::');
  const period = Number(periodStr);
  if (
    !periodStr ||
    !periodUnit ||
    Number.isNaN(period) ||
    !isPeriodUnit(periodUnit)
  ) {
    return undefined;
  }
  return { period, period_unit: periodUnit };
};

export const formatFrequencyLabel = (key: string): string => {
  const parsed = parseFrequencyKey(key);
  if (!parsed) return key;
  const labels = PERIOD_UNIT_LABELS[parsed.period_unit];
  if (parsed.period === 1) {
    return labels?.one ?? `Every ${parsed.period_unit}`;
  }
  const many = labels?.many ?? `${parsed.period_unit}s`;
  return `Every ${parsed.period} ${many}`;
};

export const findMatchingItemPrice = (
  prices: Price[],
  args: {
    itemId: string;
    period: number;
    periodUnit: PeriodUnit;
    currency: string;
  }
): Price | undefined => {
  const matches = prices.filter(
    (price) =>
      price.billing_system_item_id === args.itemId &&
      price.period === args.period &&
      price.period_unit === args.periodUnit &&
      price.currency === args.currency
  );
  return matches.find((price) => price.active !== false) ?? matches[0];
};

export const findItemPriceById = (
  prices: Price[],
  planId: string
): Price | undefined =>
  prices.find(
    (price) => price.billing_system_id === planId || price.id === planId
  );

/** Frequencies, and currencies for an optional frequency, that actually exist on the given price rows. */
export const catalogFromPrices = (
  prices: Price[],
  args?: { frequencyKey?: string }
): { frequencies: string[]; currencies: string[] } => {
  const parsedFrequency = parseFrequencyKey(args?.frequencyKey);
  const frequencies = new Set<string>();
  const currencies = new Set<string>();
  for (const row of prices) {
    if (row.period == null || !row.period_unit || !row.currency) continue;
    const frequencyKey = toFrequencyKey(row.period, row.period_unit);
    frequencies.add(frequencyKey);
    const matchesFrequency =
      !parsedFrequency ||
      (row.period === parsedFrequency.period &&
        row.period_unit === parsedFrequency.period_unit);
    if (matchesFrequency) currencies.add(row.currency);
  }
  return {
    frequencies: [...frequencies].sort(),
    currencies: [...currencies].sort(),
  };
};

/** Chargebee maps a missing price_variant_id to "default". Show the picker when a named variant exists, or an existing offer already stored a named pin. Auto is "-1"; Default is sent as "". */
export const hasNamedPriceVariants = (
  variants: string[] | undefined
): boolean => (variants ?? []).some((id) => id !== DEFAULT_PRICE_VARIANT_ID);

/** Auto is only a choice when the variant dropdown is shown. Otherwise send Default. */
export const defaultPriceVariantForItem = (
  variants: string[] | undefined
): string =>
  hasNamedPriceVariants(variants)
    ? DYNAMIC_OFFER_AUTO_VALUE
    : DEFAULT_PRICE_VARIANT_VALUE;

export const isAutoScope = (value?: string | null): boolean =>
  value === DYNAMIC_OFFER_AUTO_VALUE;

export const toCatalogPriceVariantId = (
  saved?: string | null
): string | undefined => {
  if (isAutoScope(saved)) return undefined;
  if (
    !saved ||
    saved === DEFAULT_PRICE_VARIANT_VALUE ||
    saved === DEFAULT_PRICE_VARIANT_ID
  ) {
    return DEFAULT_PRICE_VARIANT_ID;
  }
  return saved;
};

export const toPriceVariantSelectValue = (saved?: string | null): string => {
  if (isAutoScope(saved)) return DYNAMIC_OFFER_AUTO_VALUE;
  if (!saved || saved === DEFAULT_PRICE_VARIANT_ID) {
    return DEFAULT_PRICE_VARIANT_VALUE;
  }
  return saved;
};

export const toCurrencySelectValue = (saved?: string | null): string =>
  !saved || isAutoScope(saved) ? DYNAMIC_OFFER_AUTO_VALUE : saved;

export const shouldShowPriceVariantSelect = (
  variants: string[] | undefined,
  savedPriceVariant?: string
): boolean =>
  hasNamedPriceVariants(variants) ||
  (!!savedPriceVariant &&
    !isAutoScope(savedPriceVariant) &&
    savedPriceVariant !== DEFAULT_PRICE_VARIANT_VALUE);

export const formatPriceVariantLabel = (variantId: string): string =>
  variantId === DEFAULT_PRICE_VARIANT_ID ||
  variantId === DEFAULT_PRICE_VARIANT_VALUE
    ? 'Default'
    : variantId;
